import React, { useEffect, useRef } from "react";
import { Clock } from "lucide-react";

/**
 * Modal dialog displayed when a user has been inactive for the configured threshold.
 * Informs the user of impending logout and provides a countdown with a "Stay Logged In" action.
 */
export default function InactivityWarningModal({ isOpen, countdown = 60, onStayLoggedIn }) {
  const stayButtonRef = useRef(null);

  // Automatically focus the primary action button when modal opens for keyboard accessibility
  useEffect(() => {
    if (isOpen) {
      stayButtonRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inactivity-warning-title"
      aria-describedby="inactivity-warning-description"
    >
      <div className="bg-white border border-slate-200/90 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col p-6 sm:p-8 text-center animate-in zoom-in-95 duration-200">
        <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200/80 text-amber-600 flex items-center justify-center mx-auto mb-4 shadow-sm">
          <Clock className="w-7 h-7" />
        </div>

        <h2
          id="inactivity-warning-title"
          className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 tracking-tight"
        >
          Session Expiring Due to Inactivity
        </h2>

        <p
          id="inactivity-warning-description"
          className="text-sm text-slate-600 leading-relaxed mb-4"
        >
          You will be logged out due to inactivity in{" "}
          <span className="font-semibold text-slate-900 font-mono text-base">
            {countdown} second{countdown === 1 ? "" : "s"}
          </span>
          .
        </p>

        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl py-3 px-4 mb-6">
          <span className="text-3xl sm:text-4xl font-extrabold text-[#033F99] font-mono tracking-tight">
            {countdown}s
          </span>
        </div>

        <button
          ref={stayButtonRef}
          type="button"
          onClick={onStayLoggedIn}
          className="w-full bg-[#033F99] hover:bg-[#022D6E] text-white font-bold py-3 rounded-xl shadow-lg shadow-[#033F99]/20 transition-all flex items-center justify-center gap-2 text-sm cursor-pointer"
        >
          Stay Logged In
        </button>
      </div>
    </div>
  );
}
